// Waste-outlet domain: pickup orders, deliveries, collection sites,
// containers, sensors, material types / basket and the waste catalog.

import type { OrderSchema } from '../../types/order.type';
import { DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID, demoError, paginate, parseQueryParam, type DemoCtx, type DemoRouteDef } from '../core';
import { ensureDemoData } from '../data';
import {
  addBasketMaterial,
  catalogOptions,
  listMaterialTypes,
  materialName,
  organizationWasteTypes,
  removeBasketMaterial,
  replaceBasket,
  toMaterialType,
  updateBasketMaterial,
} from '../data/catalog';
import { bodyOf, includesText } from '../data/common';
import { addNotification } from '../data/notifications';
import { allOrders, containersAwaitingPickup, createOrder, deleteOrder, orderById, updateOrder } from '../data/orders';
import { organizationById } from '../data/people';
import {
  addSensorReading,
  calibrateSensor,
  containerRecordById,
  createContainer,
  createSensor,
  createSite,
  deleteContainer,
  deleteSensor,
  deleteSite,
  getSite,
  listContainerRecords,
  listSensors,
  listSites,
  sensorBattery,
  sensorById,
  sensorHealth,
  sensorReadings,
  serializeContainer,
  siteDisplayName,
  siteRecordById,
  updateContainer,
  updateSensor,
  updateSite,
} from '../data/sites';
import { deliveryDetails, toOrderSchema } from '../data/views';

function notFound(what: string) {
  return demoError(404, `${what} not found`);
}

function q(ctx: Pick<DemoCtx, 'query'>, key: string): string | undefined {
  return parseQueryParam(ctx, key);
}

function orgOf(ctx: DemoCtx): string {
  return q(ctx, 'organization_id') ?? ctx.orgId ?? DEMO_OUTLET_ORG_ID;
}

const SORTERS: Record<string, (o: OrderSchema) => string | number> = {
  created_at: (o) => o.created_at,
  updated_at: (o) => o.updated_at,
  amount: (o) => o.amount,
  sale_amount: (o) => o.sale_amount,
  status: (o) => o.status,
  waste_type: (o) => o.waste_type,
};

function listPickupOrders(ctx: DemoCtx) {
  const orgId = orgOf(ctx);
  const status = q(ctx, 'status');
  const wasteType = q(ctx, 'waste_type');
  const search = q(ctx, 'search');
  const start = q(ctx, 'start_date')?.slice(0, 10);
  const end = q(ctx, 'end_date')?.slice(0, 10);
  const rows = allOrders()
    .filter((o) => o.organization_id === orgId)
    .filter((o) => !status || o.status === status)
    .filter((o) => !start || o.created_at.slice(0, 10) >= start)
    .filter((o) => !end || o.created_at.slice(0, 10) <= end)
    .map(toOrderSchema)
    .filter((o) => !wasteType || o.waste_type === wasteType)
    .filter((o) =>
      includesText(
        [o.id, String(o.data.reference ?? ''), o.waste_type, o.material_type?.name, o.collection_site?.data?.name as string | undefined, o.status],
        search,
      ),
    );
  const key = q(ctx, 'sort_by') ?? 'created_at';
  const dir = q(ctx, 'sort_pickup_order') === 'asc' ? 1 : -1;
  const by = SORTERS[key] ?? SORTERS.created_at;
  rows.sort((a, b) => {
    const x = by(a);
    const y = by(b);
    return (x < y ? -1 : x > y ? 1 : 0) * dir;
  });
  const page = paginate(rows, ctx.query.page ?? 1, ctx.query.limit ?? 20);
  return { result: { pickup_orders: page.items, total: page.total, page: page.page, limit: page.limit } };
}

export const outletRoutes: DemoRouteDef[] = [
  // ── Pickup orders ──
  ['get', '/pickup_order', (ctx) => {
    ensureDemoData();
    return listPickupOrders(ctx);
  }],
  ['post', '/pickup_order', ({ body }) => {
    ensureDemoData();
    const input = bodyOf(body);
    if (!input.material_type_id) return demoError(422, 'material_type_id is required');
    const order = createOrder(input);
    const org = organizationById(order.organization_id);
    addNotification(
      DEMO_RECYCLER_ORG_ID,
      ['New pickup request', 'طلب استلام جديد'],
      [
        `${org?.name ?? 'Outlet'} ${siteDisplayName(order.site_id) ?? ''} requested a ${materialName(order.material_id)} pickup (${order.reference}).`,
        `طلب ${org?.name_ar ?? org?.name ?? ''} استلام ${materialName(order.material_id)} (${order.reference}).`,
      ],
      { pickup_order_id: order.id },
    );
    return { result: toOrderSchema(order) };
  }],
  ['get', '/pickup_order/:orderId', ({ params }) => {
    ensureDemoData();
    const order = orderById(params.orderId);
    return order ? { result: toOrderSchema(order) } : notFound('Pickup order');
  }],
  ['put', '/pickup_order/:orderId', ({ params, body }) => {
    ensureDemoData();
    const order = updateOrder(params.orderId, bodyOf(body));
    return order ? { result: toOrderSchema(order) } : notFound('Pickup order');
  }],
  ['delete', '/pickup_order/:orderId', ({ params }) => {
    ensureDemoData();
    return deleteOrder(params.orderId) ? { result: { success: true, id: params.orderId } } : notFound('Pickup order');
  }],
  ['get', '/delivery/pickup-order/:orderId', ({ params }) => {
    ensureDemoData();
    const order = orderById(params.orderId);
    if (!order) return notFound('Pickup order');
    const details = deliveryDetails(order);
    return details ? { result: details } : demoError(404, 'No delivery has been created for this pickup order yet');
  }],

  // ── Collection sites (GET /collection_site/{id} serves both an org list and one site) ──
  ['get', '/collection_site/:id', ({ params, query }) => {
    ensureDemoData();
    if (organizationById(params.id)) {
      const rows = listSites(params.id, parseQueryParam({ query }, 'search'));
      return { result: rows, total: rows.length };
    }
    const site = getSite(params.id);
    return site ? { result: site } : notFound('Collection site');
  }],
  ['post', '/collection_site', ({ body }) => {
    ensureDemoData();
    return { result: createSite(bodyOf(body)) };
  }],
  ['put', '/collection_site/:siteId', ({ params, body }) => {
    ensureDemoData();
    const site = updateSite(params.siteId, bodyOf(body));
    return site ? { result: site } : notFound('Collection site');
  }],
  ['delete', '/collection_site/:siteId', ({ params }) => {
    ensureDemoData();
    return deleteSite(params.siteId) ? { result: { success: true } } : notFound('Collection site');
  }],

  // ── Containers (GET /container/{id} serves both an org list and one container) ──
  ['get', '/container/:id', (ctx) => {
    ensureDemoData();
    const { params } = ctx;
    const awaiting = containersAwaitingPickup();
    if (organizationById(params.id)) {
      const search = q(ctx, 'search');
      const status = q(ctx, 'status');
      const materialId = q(ctx, 'material_type_id');
      const siteId = q(ctx, 'collection_site_id');
      const rows = listContainerRecords(params.id)
        .map((c) => serializeContainer(c, awaiting))
        .filter((c) => !status || c.status === status)
        .filter((c) => !materialId || c.material_type_id === materialId)
        .filter((c) => !siteId || c.collection_site_id === siteId)
        .filter((c) => includesText([c.device_id, c.data?.name as string, c.data?.location_name as string, c.material_type?.name, siteDisplayName(c.collection_site_id)], search));
      // The store reads { containers, total }; the OpenAPI contract is { result }. Serve both.
      return { result: rows, containers: rows, total: rows.length };
    }
    const c = containerRecordById(params.id);
    return c ? { result: serializeContainer(c, awaiting) } : notFound('Container');
  }],
  ['post', '/container', ({ body }) => {
    ensureDemoData();
    const input = bodyOf(body);
    if (input.collection_site_id && !siteRecordById(String(input.collection_site_id))) return demoError(422, 'Unknown collection site');
    return { result: serializeContainer(createContainer(input), containersAwaitingPickup()) };
  }],
  ['put', '/container/:containerId', ({ params, body }) => {
    ensureDemoData();
    const c = updateContainer(params.containerId, bodyOf(body));
    return c ? { result: serializeContainer(c, containersAwaitingPickup()) } : notFound('Container');
  }],
  ['delete', '/container/:containerId', ({ params }) => {
    ensureDemoData();
    return deleteContainer(params.containerId) ? { result: { success: true } } : notFound('Container');
  }],

  // ── IoT sensors ──
  ['get', '/sensors/organization/:orgId', (ctx) => {
    ensureDemoData();
    const rows = listSensors(ctx.params.orgId, { search: q(ctx, 'search'), status: q(ctx, 'status'), type: q(ctx, 'type') });
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: { sensors: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['post', '/sensors', ({ body }) => {
    ensureDemoData();
    return { result: createSensor(bodyOf(body)) };
  }],
  ['post', '/sensors/:sensorId/calibrate', ({ params }) => {
    ensureDemoData();
    const s = calibrateSensor(params.sensorId);
    return s ? { result: s } : notFound('Sensor');
  }],
  ['get', '/sensors/:sensorId/readings', ({ params }) => {
    ensureDemoData();
    return sensorById(params.sensorId) ? { result: sensorReadings(params.sensorId) } : notFound('Sensor');
  }],
  ['post', '/sensors/:sensorId/readings', ({ params, body }) => {
    ensureDemoData();
    const s = addSensorReading(params.sensorId, bodyOf(body) as never);
    return s ? { result: s } : notFound('Sensor');
  }],
  ['get', '/sensors/:sensorId/health', ({ params }) => {
    ensureDemoData();
    const h = sensorHealth(params.sensorId);
    return h ? { result: h } : notFound('Sensor');
  }],
  ['get', '/sensors/:sensorId/battery', ({ params }) => {
    ensureDemoData();
    const b = sensorBattery(params.sensorId);
    return b ? { result: b } : notFound('Sensor');
  }],
  ['get', '/sensors/:sensorId', ({ params }) => {
    ensureDemoData();
    const s = sensorById(params.sensorId);
    return s ? { result: s } : notFound('Sensor');
  }],
  ['put', '/sensors/:sensorId', ({ params, body }) => {
    ensureDemoData();
    const s = updateSensor(params.sensorId, bodyOf(body));
    return s ? { result: s } : notFound('Sensor');
  }],
  ['delete', '/sensors/:sensorId', ({ params }) => {
    ensureDemoData();
    return deleteSensor(params.sensorId) ? { result: { success: true } } : notFound('Sensor');
  }],

  // ── Material types (the organization's basket as aliases) ──
  ['get', '/material_type/list/:orgId', ({ params, query }) => {
    ensureDemoData();
    return { result: listMaterialTypes(params.orgId, parseQueryParam({ query }, 'search')) };
  }],
  ['post', '/material_type', ({ body, orgId }) => {
    ensureDemoData();
    const b = bodyOf(body);
    const org = String(b.organization_id ?? orgId ?? DEMO_OUTLET_ORG_ID);
    // Organizations pick from the shared catalog; a free-text name maps onto the closest catalog row.
    const picked = listMaterialTypes(org).find((m) => m.name.toLowerCase() === String(b.name ?? '').toLowerCase());
    return picked ? { result: picked } : demoError(422, 'Pick a waste type from the catalog');
  }],
  ['get', '/material_type/:materialTypeId', ({ params, orgId }) => ({ result: toMaterialType(params.materialTypeId, orgId) })],
  ['put', '/material_type/:materialTypeId', ({ params, orgId }) => {
    ensureDemoData();
    return { result: toMaterialType(params.materialTypeId, orgId) };
  }],
  ['delete', '/material_type/:materialTypeId', ({ params, orgId }) => {
    ensureDemoData();
    return removeBasketMaterial(orgId ?? DEMO_OUTLET_ORG_ID, params.materialTypeId) ? { result: { success: true } } : notFound('Material type');
  }],
  ['get', '/organization/:orgId/waste-types', ({ params }) => {
    ensureDemoData();
    return { result: organizationWasteTypes(params.orgId) };
  }],
  ['post', '/organization/:orgId/waste-types', ({ params, body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    const res = addBasketMaterial(params.orgId, String(b.material_id ?? ''), (b.client_custom_id as string | null) ?? null);
    if (res === 'conflict') return demoError(409, 'This waste type is already selected');
    if (res === 'unknown') return demoError(404, 'Waste type not found in the catalog');
    return { result: organizationWasteTypes(params.orgId).find((r) => r.material_id === res.material_id) };
  }],
  ['put', '/organization/:orgId/waste-types', ({ params, body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    replaceBasket(params.orgId, Array.isArray(b.material_ids) ? (b.material_ids as string[]) : []);
    return { result: organizationWasteTypes(params.orgId) };
  }],
  ['patch', '/organization/:orgId/waste-types/:materialId', ({ params, body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    const row = updateBasketMaterial(params.orgId, params.materialId, (b.client_custom_id as string | null) ?? null);
    return row ? { result: organizationWasteTypes(params.orgId).find((r) => r.material_id === row.material_id) } : notFound('Waste type');
  }],
  ['delete', '/organization/:orgId/waste-types/:materialId', ({ params }) => {
    ensureDemoData();
    return removeBasketMaterial(params.orgId, params.materialId) ? { result: { success: true } } : notFound('Waste type');
  }],

  // ── Waste catalog ──
  ['get', '/waste-catalog/options', (ctx) => {
    ensureDemoData();
    const includeAll = ['true', '1'].includes(String(q(ctx, 'include_all_materials') ?? ''));
    return { result: catalogOptions(q(ctx, 'organization_id'), includeAll) };
  }],
];
